import dayjs from "dayjs";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const getType = (item) =>
  item?.type || item?.subcategory || item?.category || null;

// --- Baseline chooser --------------------------------------------------------
function pickOriginalUnitPrice(it) {
  const candidates = {
    baseUnitPrice: it.baseUnitPrice,
    originalUnitPrice: it.originalUnitPrice,
    priceBeforeDiscount: it.priceBeforeDiscount,
    compareAtPrice: it.compareAtPrice,
    msrp: it.msrp,
    regularPrice: it.regularPrice,
    unitPriceOriginal: it.unitPriceOriginal,
    _originalUnitPrice: it._originalUnitPrice,
    unitPrice: it.unitPrice, // LAST resort
  };

  for (const key of Object.keys(candidates)) {
    const v = candidates[key];
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) {
      return n;
    }
  }
  return 0;
}

// --- Main --------------------------------------------------------------------
export function applyDiscountsToCart(
  cartItems,
  user,
  discountRules,
  couponCode
) {
  const now = dayjs();

  const validDiscounts = (discountRules || []).filter((rule) => {
    const isCouponMatch =
      !!couponCode &&
      !!rule.couponCode &&
      rule.couponCode.toUpperCase() === couponCode.toUpperCase();

    if (!rule?.enabled) return false;
    if (!(rule.autoApply || isCouponMatch)) return false;

    if (
      rule?.conditions?.startDate &&
      now.isBefore(dayjs(rule.conditions.startDate))
    )
      return false;
    if (
      rule?.conditions?.endDate &&
      now.isAfter(dayjs(rule.conditions.endDate))
    )
      return false;

    if (rule?.appliesTo?.guestOnly && !user?.isGuest) return false;

    if (
      Array.isArray(rule?.appliesTo?.userTags) &&
      rule.appliesTo.userTags.length > 0 &&
      !rule.appliesTo.userTags.some((t) => (user?.tags || []).includes(t))
    )
      return false;

    return true;
  });

  validDiscounts.sort(
    (a, b) => (b?.stacking?.priority || 0) - (a?.stacking?.priority || 0)
  );

  // 🔁 Fresh baseline every run
  const updatedItems = (cartItems || []).map((it) => {
    const qty = Math.max(1, Number(it?.quantity || 1));
    // **** FIX: honor baseUnitPrice (or other candidates) ****
    const originalUnit = pickOriginalUnitPrice(it);

    const out = {
      ...it,
      type: getType(it),
      quantity: qty,
      unitPrice: round2(originalUnit), // baseline unit
      totalPrice: round2(originalUnit * qty), // baseline line
      // clear transient fields
      discountAmount: 0,
      discountType: null,
      discountName: null,
      discountsApplied: [],
      // persist original within this engine’s result
      _originalUnitPrice: originalUnit,
      _originalLineTotal: round2(originalUnit * qty),
    };

    return out;
  });

  const fullDiscountRules = [];
  const displayDiscounts = [];
  const appliedIds = new Set();

  for (const rule of validDiscounts) {
    const exclusiveActive = fullDiscountRules.some(
      (d) => d?.stacking?.exclusive
    );
    const currentIsExclusive = !!rule?.stacking?.exclusive;
    const conflicts =
      Array.isArray(rule?.stacking?.conflictsWith) &&
      rule.stacking.conflictsWith.some((id) => appliedIds.has(id.toString()));

    if ((exclusiveActive || conflicts) && !currentIsExclusive) continue;

    const result = applyRuleToItems(rule, updatedItems);
    if (result?.applied) {
      fullDiscountRules.push(rule);

      const amountSaved = updatedItems.reduce((acc, item) => {
        const parts = (item.discountsApplied || []).filter(
          (d) => d.name === rule.name
        );
        const sum = parts.reduce((s, d) => s + Number(d.amount || 0), 0);
        return acc + sum;
      }, 0);

      displayDiscounts.push({
        _id: rule._id,
        name: rule.name,
        type: rule.type,
        value: rule?.config?.discountValue,
        valueType: rule?.config?.discountType,
        category: rule?.appliesTo?.collectionTypes?.[0] || null,
        subcategory: rule?.appliesTo?.subcategories?.[0] || null,
        couponCode: rule?.couponCode || null,
        amount: round2(amountSaved || 0),
      });

      appliedIds.add(rule._id?.toString());
      if (currentIsExclusive) break;
    }
  }

  const originalSubtotal = updatedItems.reduce(
    (sum, it) => sum + Number(it._originalLineTotal || 0),
    0
  );

  const totalDiscount = updatedItems.reduce((sum, item) => {
    const orig = Number(item._originalLineTotal || 0);
    return sum + (orig - Number(item.totalPrice || 0));
  }, 0);

  return {
    updatedItems,
    originalSubtotal: round2(originalSubtotal),
    totalDiscount: round2(totalDiscount || 0),
    appliedDiscounts: displayDiscounts,
    internalDiscountMetadata: fullDiscountRules,
  };
}

// --- Rule application helpers -------------------------------------------------
function applyRuleToItems(rule, items) {
  switch (rule.type) {
    case "category_discount":
      return applyCategoryDiscount(rule, items);
    case "subcategory_discount":
      return applySubcategoryDiscount(rule, items);
    case "product_discount":
      return applyProductDiscount(rule, items);
    case "newsletter_signup":
      return applyNewsletterSignupDiscount(rule, items);
    default:
      return { applied: false };
  }
}

function applyDiscountToMatchingItems(rule, items, matcherFn) {
  let applied = false;

  for (const item of items) {
    if (!matcherFn(item)) continue;

    const qty = Math.max(1, Number(item.quantity || 1));
    const currentUnit = Number(item.unitPrice || 0);

    const unitDiscount =
      rule.config.discountType === "percentage"
        ? (currentUnit * Number(rule.config.discountValue || 0)) / 100
        : Number(rule.config.discountValue || 0);

    const discountedUnit = Math.max(0, currentUnit - unitDiscount);
    const lineDiscount = round2(unitDiscount * qty);

    item.unitPrice = round2(discountedUnit);
    item.totalPrice = round2(discountedUnit * qty);
    item.discountAmount = round2((item.discountAmount || 0) + lineDiscount);
    item.discountType = rule.config.discountType;
    item.discountName = rule.name;

    item.discountsApplied.push({
      ruleId: rule._id?.toString?.() || null,
      name: rule.name,
      type: rule.type,
      valueType: rule.config.discountType,
      value: rule.config.discountValue,
      priority: rule?.stacking?.priority || 0,
      exclusive: !!rule?.stacking?.exclusive,
      amount: lineDiscount,
      matchedOn: {
        productType: rule?.appliesTo?.productTargets?.[0]?.productType || null,
        category: rule?.appliesTo?.collectionTypes?.[0] || null,
        subcategory: rule?.appliesTo?.subcategories?.[0] || null,
      },
    });

    applied = true;
  }

  return { applied };
}

function applyCategoryDiscount(rule, items) {
  const set = rule.appliesTo?.collectionTypes || [];
  return applyDiscountToMatchingItems(rule, items, (item) =>
    set.includes(getType(item))
  );
}

function applySubcategoryDiscount(rule, items) {
  const set = rule.appliesTo?.subcategories || [];
  return applyDiscountToMatchingItems(rule, items, (item) =>
    set.includes(getType(item))
  );
}

function applyProductDiscount(rule, items) {
  const targets = rule.appliesTo?.productTargets || [];
  return applyDiscountToMatchingItems(rule, items, (item) => {
    const type = getType(item);
    const idStr = (item._id || item.productId)?.toString?.();
    return targets.some(
      (t) => t.productType === type && t.productId?.toString?.() === idStr
    );
  });
}

function applyNewsletterSignupDiscount(rule, items) {
  let applied = false;
  const totalBefore = items.reduce(
    (sum, it) => sum + it.unitPrice * it.quantity,
    0
  );
  if (totalBefore <= 0) return { applied: false };

  const dv = Number(rule.config.discountValue || 0);
  const totalDiscount =
    rule.config.discountType === "percentage" ? (totalBefore * dv) / 100 : dv;

  for (const item of items) {
    const itemTotal = item.unitPrice * item.quantity;
    const share = itemTotal / totalBefore;
    const itemDiscount = round2(totalDiscount * share);

    item.totalPrice = Math.max(0, round2(itemTotal - itemDiscount));
    item.discountAmount = round2((item.discountAmount || 0) + itemDiscount);
    item.discountType = rule.config.discountType;
    item.discountName = rule.name;

    item.discountsApplied.push({
      ruleId: rule._id?.toString?.() || null,
      name: rule.name,
      type: rule.type,
      valueType: rule.config.discountType,
      value: rule.config.discountValue,
      priority: rule?.stacking?.priority || 0,
      exclusive: !!rule?.stacking?.exclusive,
      amount: itemDiscount,
      matchedOn: { productType: null, category: null, subcategory: null },
    });

    applied = true;
  }
  return { applied };
}
